// src/Product/CustomerMiniReportModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X, RefreshCcw, FileText, Wallet, AlertCircle } from "lucide-react";
import { supabase } from "../Services/supabaseClient";
import "../Product/customers.css";

function money(n) {
  const v = Number(n || 0);
  return `₦${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function toDateLabel(d) {
  return d ? String(d) : "—";
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (["paid", "complete", "completed"].includes(s)) return "success";
  if (["partial", "part-paid", "partially_paid"].includes(s)) return "warning";
  if (["overdue", "unpaid", "open"].includes(s)) return "danger";
  return "neutral";
}

function MiniStatCard({ icon: Icon, label, value, hint, tone = "neutral" }) {
  return (
    <div className={`cmr-stat-card ${tone}`}>
      <div className="cmr-stat-icon">
        <Icon size={18} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

function ReportTable({ title, subtitle, children }) {
  return (
    <section className="cmr-section">
      <div className="cmr-section-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function CustomerMiniReportModal({
  open,
  onClose,
  companyId,
  customer,
  fromDate,
  toDate,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [invoiceRows, setInvoiceRows] = useState([]);
  const [saleRows, setSaleRows] = useState([]);

  async function load() {
    if (!open) return;

    const customerId = customer?.customer_id || customer?.id;
    if (!companyId || !customerId) return;

    setBusy(true);
    setErr("");

    try {
      let invQ = supabase
        .from("invoices")
        .select('id,invoiceNumber,date,"expiryDate",status,total,created_at')
        .eq("company_id", companyId)
        .eq("customer_id", customerId)
        .order("date", { ascending: false })
        .limit(500);

      if (fromDate) invQ = invQ.gte("date", fromDate);
      if (toDate) invQ = invQ.lte("date", toDate);

      const { data: inv, error: invErr } = await invQ;
      if (invErr) throw invErr;

      const invIds = (inv || []).map((x) => x.id);

      let paysByInv = {};
      if (invIds.length) {
        const { data: pays, error: payErr } = await supabase
          .from("invoice_payments")
          .select("id,invoice_id,payment_date,type,amount,notes,created_at")
          .eq("company_id", companyId)
          .in("invoice_id", invIds)
          .order("payment_date", { ascending: false });

        if (payErr) throw payErr;

        (pays || []).forEach((p) => {
          (paysByInv[p.invoice_id] ||= []).push(p);
        });
      }

      const invComputed = (inv || []).map((row) => {
        const status = String(row.status || "open").toLowerCase();
        const pays = paysByInv[row.id] || [];

        const paidFromPayments = pays
          .filter((p) => String(p.type || "").toUpperCase() === "PAYMENT")
          .reduce((s, p) => s + Number(p.amount || 0), 0);

        const refunded = pays
          .filter((p) => String(p.type || "").toUpperCase() === "REFUND")
          .reduce((s, p) => s - Number(p.amount || 0), 0);

        const total = Number(row.total || 0);
        const paid = status === "paid" ? total : paidFromPayments;
        const balance =
          status === "paid" ? 0 : total - (paidFromPayments - refunded);

        return { ...row, paid, refunded, balance };
      });

      setInvoiceRows(invComputed);

      let saleQ = supabase
        .from("sales_transactions")
        .select("id,sale_date,total,payment_status")
        .eq("company_id", companyId)
        .eq("customer_id", customerId)
        .order("sale_date", { ascending: false })
        .limit(500);

      if (fromDate) saleQ = saleQ.gte("sale_date", fromDate);
      if (toDate) saleQ = saleQ.lte("sale_date", toDate);

      const { data: sales, error: s2Err } = await saleQ;
      if (s2Err) throw s2Err;

      const saleIds = (sales || []).map((x) => x.id);

      let setsBySale = {};
      if (saleIds.length) {
        const { data: sets, error: setErr } = await supabase
          .from("sale_settlements")
          .select("id,sale_id,type,amount,payment_date,notes,created_at")
          .eq("company_id", companyId)
          .in("sale_id", saleIds)
          .order("payment_date", { ascending: false });

        if (setErr) throw setErr;

        (sets || []).forEach((p) => {
          (setsBySale[p.sale_id] ||= []).push(p);
        });
      }

      const saleComputed = (sales || []).map((row) => {
        const sets = setsBySale[row.id] || [];

        const paidFromSets = sets
          .filter((p) => String(p.type || "").toUpperCase() === "PAYMENT")
          .reduce((s, p) => s + Number(p.amount || 0), 0);

        const refunded = sets
          .filter((p) => String(p.type || "").toUpperCase() === "REFUND")
          .reduce((s, p) => s - Number(p.amount || 0), 0);

        const total = Number(row.total || 0);
        const status = String(row.payment_status || "UNPAID").toUpperCase();

        const basePaid = status === "PAID" ? total : 0;
        const paid = basePaid + paidFromSets;
        const balance = total - (paid - refunded);

        return { ...row, paid, refunded, balance };
      });

      setSaleRows(saleComputed);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to load customer report.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, customer?.customer_id, customer?.id, fromDate, toDate]);

  const invoiceSubtotals = useMemo(() => {
    const list = invoiceRows || [];
    return {
      count: list.length,
      total: list.reduce((s, r) => s + Number(r.total || 0), 0),
      paid: list.reduce((s, r) => s + Number(r.paid || 0), 0),
      refunded: list.reduce((s, r) => s + Number(r.refunded || 0), 0),
      balance: list.reduce((s, r) => s + Number(r.balance || 0), 0),
    };
  }, [invoiceRows]);

  const salesSubtotals = useMemo(() => {
    const list = saleRows || [];
    return {
      count: list.length,
      total: list.reduce((s, r) => s + Number(r.total || 0), 0),
      paid: list.reduce((s, r) => s + Number(r.paid || 0), 0),
      refunded: list.reduce((s, r) => s + Number(r.refunded || 0), 0),
      balance: list.reduce((s, r) => s + Number(r.balance || 0), 0),
    };
  }, [saleRows]);

  const grandTotals = useMemo(() => {
    const invoiced = invoiceSubtotals.total + salesSubtotals.total;
    const paid = invoiceSubtotals.paid + salesSubtotals.paid;
    const refunded = invoiceSubtotals.refunded + salesSubtotals.refunded;
    const balance = invoiceSubtotals.balance + salesSubtotals.balance;
    const collectionRate = invoiced > 0 ? (paid / invoiced) * 100 : 0;

    return {
      transactionCount: invoiceSubtotals.count + salesSubtotals.count,
      invoiced,
      paid,
      refunded,
      balance,
      collectionRate,
    };
  }, [invoiceSubtotals, salesSubtotals]);

  if (!open) return null;

  return (
    <div
      className="cmr-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="cmr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cmr-topbar">
          <div>
            <p className="cmr-eyebrow">Customer Statement Summary</p>
            <h2>{customer?.customer_name || "Unnamed Customer"}</h2>
            <p className="cmr-muted">
              {customer?.company_name ? `${customer.company_name} • ` : ""}
              Period: <strong>{fromDate || "All dates"}</strong> to{" "}
              <strong>{toDate || "All dates"}</strong>
            </p>
          </div>

          <div className="cmr-actions">
            <button className="cmr-btn ghost" onClick={load} disabled={busy}>
              <RefreshCcw size={16} />
              Refresh
            </button>
            <button className="cmr-icon-btn" onClick={onClose} disabled={busy}>
              <X size={18} />
            </button>
          </div>
        </div>

        {err && (
          <div className="cmr-banner error">
            <AlertCircle size={16} />
            {err}
          </div>
        )}

        <div className="cmr-note">
          This report summarizes invoices, quick sales, payments, refunds and
          outstanding balance for this customer. Balances shown here support
          receivables review and customer follow-up.
        </div>

        <div className="cmr-stat-grid">
          <MiniStatCard
            icon={FileText}
            label="Total Billed"
            value={money(grandTotals.invoiced)}
            hint={`${grandTotals.transactionCount} transaction(s)`}
          />
          <MiniStatCard
            icon={Wallet}
            label="Total Paid"
            value={money(grandTotals.paid)}
            hint={`${grandTotals.collectionRate.toFixed(1)}% collection rate`}
            tone="success"
          />
          <MiniStatCard
            icon={RefreshCcw}
            label="Refunded"
            value={money(grandTotals.refunded)}
            hint="Credit returned to customer"
            tone="warning"
          />
          <MiniStatCard
            icon={AlertCircle}
            label="Balance Due"
            value={money(grandTotals.balance)}
            hint={
              grandTotals.balance > 0
                ? "Amount still outstanding"
                : "No outstanding balance"
            }
            tone={grandTotals.balance > 0 ? "danger" : "success"}
          />
        </div>

        {busy && <div className="cmr-loading">Loading customer report…</div>}

        <div className="cmr-split-summary">
          <div>
            <span>Invoices</span>
            <strong>{invoiceSubtotals.count}</strong>
            <small>{money(invoiceSubtotals.balance)} outstanding</small>
          </div>
          <div>
            <span>Quick Sales</span>
            <strong>{salesSubtotals.count}</strong>
            <small>{money(salesSubtotals.balance)} outstanding</small>
          </div>
        </div>

        <ReportTable
          title="Invoices"
          subtitle="Formal invoices issued to this customer within the selected period."
        >
          <div className="cmr-table-wrap">
            <table className="cmr-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th className="num">Total</th>
                  <th className="num">Paid</th>
                  <th className="num">Refunded</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>

              <tbody>
                {invoiceRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.invoiceNumber || "—"}</strong>
                    </td>
                    <td>{toDateLabel(r.date)}</td>
                    <td>{toDateLabel(r.expiryDate)}</td>
                    <td>
                      <span className={`cmr-pill ${statusClass(r.status)}`}>
                        {String(r.status || "open").toUpperCase()}
                      </span>
                    </td>
                    <td className="num">{money(r.total)}</td>
                    <td className="num success-text">{money(r.paid)}</td>
                    <td className="num warning-text">{money(r.refunded)}</td>
                    <td
                      className={`num ${
                        Number(r.balance || 0) > 0 ? "danger-text" : ""
                      }`}
                    >
                      <strong>{money(r.balance)}</strong>
                    </td>
                  </tr>
                ))}

                {!!invoiceRows.length && (
                  <tr className="cmr-total-row">
                    <td colSpan="4">Invoices Subtotal</td>
                    <td className="num">{money(invoiceSubtotals.total)}</td>
                    <td className="num">{money(invoiceSubtotals.paid)}</td>
                    <td className="num">{money(invoiceSubtotals.refunded)}</td>
                    <td className="num">{money(invoiceSubtotals.balance)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {!busy && !invoiceRows.length && (
              <div className="cmr-empty">
                No invoices found for this customer in the selected period.
              </div>
            )}
          </div>
        </ReportTable>

        <ReportTable
          title="Quick Sales"
          subtitle="Direct sales recorded outside the formal invoice workflow."
        >
          <div className="cmr-table-wrap">
            <table className="cmr-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="num">Total</th>
                  <th className="num">Paid</th>
                  <th className="num">Refunded</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>

              <tbody>
                {saleRows.map((r) => (
                  <tr key={r.id}>
                    <td>{toDateLabel(r.sale_date)}</td>
                    <td>
                      <span
                        className={`cmr-pill ${statusClass(r.payment_status)}`}
                      >
                        {String(r.payment_status || "UNPAID").toUpperCase()}
                      </span>
                    </td>
                    <td className="num">{money(r.total)}</td>
                    <td className="num success-text">{money(r.paid)}</td>
                    <td className="num warning-text">{money(r.refunded)}</td>
                    <td
                      className={`num ${
                        Number(r.balance || 0) > 0 ? "danger-text" : ""
                      }`}
                    >
                      <strong>{money(r.balance)}</strong>
                    </td>
                  </tr>
                ))}

                {!!saleRows.length && (
                  <tr className="cmr-total-row">
                    <td colSpan="2">Sales Subtotal</td>
                    <td className="num">{money(salesSubtotals.total)}</td>
                    <td className="num">{money(salesSubtotals.paid)}</td>
                    <td className="num">{money(salesSubtotals.refunded)}</td>
                    <td className="num">{money(salesSubtotals.balance)}</td>
                  </tr>
                )}

                {(invoiceRows.length || saleRows.length) && (
                  <tr className="cmr-grand-row">
                    <td colSpan="2">Grand Total</td>
                    <td className="num">{money(grandTotals.invoiced)}</td>
                    <td className="num">{money(grandTotals.paid)}</td>
                    <td className="num">{money(grandTotals.refunded)}</td>
                    <td className="num">{money(grandTotals.balance)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {!busy && !saleRows.length && (
              <div className="cmr-empty">
                No quick sales found for this customer in the selected period.
              </div>
            )}
          </div>
        </ReportTable>
      </div>
    </div>
  );
}
